// Models
import { C2PAManifest } from './media-list-item-manifest.model';
import { IntegrationProvider } from '../../../models/integration-providers.model';
import { TranscriptionResponse } from './media-upload.model';

// Shared
import { AssetsByAssetId } from '@oddr/user-schemas';

// Directives
import { SortColumn, SortDirection } from '../directives/media-list-sort.directive';

export class MediaItemIntegration {
  icon: string;
  error: string;
  madeForKids: boolean;
  provider: string;
  scheduledTime: Date;
  uploadStatus: string;
  url: string;
  visibility: string;

  constructor(integration: any) {
    this.error = integration.error;
    this.madeForKids = integration.madeForKids;

    switch (integration.provider) {
      case (IntegrationProvider.Facebook):
        this.icon = 'facebook-logo';
        this.provider = 'Facebook';
        break;
      case (IntegrationProvider.Instagram):
        this.icon = 'instagram-logo';
        this.provider = 'Instagram';
        break;
      case (IntegrationProvider.LinkedIn):
        this.icon = 'linkedin-sq';
        this.provider = 'LinkedIn';
        break;
      case (IntegrationProvider.TikTok):
        this.icon = 'tiktok-sq';
        this.provider = 'TikTok';
        break;
      case (IntegrationProvider.YouTube):
        this.icon = 'youtube-sq';
        this.provider = 'YouTube';
        break;
      case (IntegrationProvider.X):
        this.icon = 'x-logo';
        this.provider = 'X';
        break;
      default:
        this.icon = 'default-logo';
        this.provider = 'Unknown provider';
    }

    this.scheduledTime = integration.scheduledTime;
    this.uploadStatus = integration.uploadStatus;
    this.url = integration.url;
    this.visibility = integration.visibility;
  }
}

export class MediaItem {
  assetId: string;
  groupId?: string;
  dateUploaded: Date | null;
  fileName: string;
  fullName: string;
  integrations: MediaItemIntegration[];
  manifest: C2PAManifest | null;
  oddrHash: string;
  oddrLink: string;
  thumbnail: string;
  title: string;
  transcription: TranscriptionResponse;
  username: string;
  videoLinks: (string | null | undefined)[];

  constructor(response: AssetsByAssetId) {
    this.assetId = response.assetId;
    this.groupId = response.groupId;
    this.dateUploaded = response.dateUploaded ? new Date(response.dateUploaded) : null;
    this.fileName = response.signedFileName ?? '-';
    this.fullName = response.fullName;
    this.integrations = response.integrations.map(integration => new MediaItemIntegration(integration));
    this.manifest = response.manifest ? new C2PAManifest(response.manifest) : null;
    this.oddrHash = response.oddrHash ?? '';
    this.oddrLink = response.oddrLink;
    this.thumbnail = response.thumbnailUri;
    this.title = response.title;
    this.transcription = response.transcription;
    this.username = response.username;
    this.videoLinks = response.videoLinks;
  }
}

/**
 * Public collection response shape from GET ppf/assets/collection/:groupId.
 * Kept local to the app to avoid depending on backend service packages.
 */
export interface CollectionResponse {
  groupId: string;
  isCollection: boolean;
  title: string;
  description?: string;
  collectionLink?: string;
  fullName: string;
  username: string;
  integrations: any[];
  members: AssetsByAssetId[];
}

export class MediaCollection {
  groupId: string;
  isCollection: boolean;
  title: string;
  description?: string;
  collectionLink?: string;
  fullName: string;
  username: string;
  integrations: MediaItemIntegration[];
  members: MediaItem[];

  constructor(response: CollectionResponse) {
    this.groupId = response.groupId;
    this.isCollection = response.isCollection;
    this.title = response.title;
    this.description = response.description;
    this.collectionLink = response.collectionLink;
    this.fullName = response.fullName;
    this.username = response.username;
    this.integrations = response.integrations.map(i => new MediaItemIntegration(i));
    this.members = response.members.map(m => new MediaItem(m));
  }
}

export interface MediaListItem {
  assetId: string;
  datePublished: Date | null;
  dateUploaded: Date | null;
  integrations: any[];
  oddrLink: string;
  status: string;
  thumbnail: string;
  title: string;
  type: string;
  groupId?: string;
  groupIndex?: number;
  isCollection?: boolean;
  collectionMembers?: MediaListItem[];
}

export interface MediaListSortEvent {
  column: SortColumn;
  direction: SortDirection;
}
